"use client";

import { useState, useEffect } from "react";

/**
 * `null` until measured on the client — callers must treat that as "unknown"
 * (never as desktop), which is also what keeps the first client render matching
 * the server HTML.
 *
 * Pass `enabled: false` when a caller doesn't actually consume the value on this
 * render path: resolving it re-renders the whole subtree for nothing, and on a
 * page of lazily-hydrated widgets that re-render is enough to make React swap in
 * a Suspense fallback.
 */
export function useIsMobile(breakpoint: number = 768, enabled: boolean = true) {
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined" || !enabled) return;

    // Handler to call on resize
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };

    // Set initial value after mount
    handleResize();

    // Listen for resize events
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [breakpoint, enabled]);

  return isMobile;
}
